import { useEffect, useRef } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { AlertCircle, ArrowLeft, Info, Loader2 } from 'lucide-react'

import { useTextImportStatus } from '../hooks/useTextImport'
import { extractErrorMessage } from '../api/client'
import { useLocale } from '../i18n/context'

interface LocationState {
  deck_id: string
  title: string | null
  words_skipped?: number
  target_deck_title?: string | null
}

export default function TextImportStatusPage() {
  const { tr } = useLocale()
  const tis = tr.textImportStatus
  const { id: textImportId } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()

  const state = location.state as LocationState | null
  const title = state?.title ?? null
  const wordsSkipped = state?.words_skipped ?? 0
  const targetDeckTitle = state?.target_deck_title ?? null

  const { data, isError, error } = useTextImportStatus(textImportId)

  const deckId = state?.deck_id ?? data?.deck_id ?? null

  const errorCountRef = useRef(0)
  useEffect(() => {
    if (isError) errorCountRef.current += 1
    else errorCountRef.current = 0
  }, [isError])

  const status       = data?.status
  // Count created cards against what the person CONFIRMED, not against what
  // extraction proposed (words_new) — those diverge the moment anything is
  // deselected, and the screen then reported "25 / 60" for a complete import.
  // Imports predating words_confirmed report 0; fall back for those.
  const total        = (data?.words_confirmed || data?.words_new) ?? 0
  const current      = data?.words_generated ?? 0
  const pending      = data?.words_pending   ?? 0
  const failed       = data?.words_failed    ?? 0
  const reasonCode   = data?.error_message   ?? null
  // Read from the RECORD, not from router state. `wordsSkipped` above comes
  // from the create-deck response via `location.state` and is 0 on any reload,
  // deep link or resumed poll — which is exactly when the reader most needs the
  // gap between "what I picked" and "what I got" explained. The response value
  // wins whenever the record has one; the router value stays as the fallback
  // for a backend that predates the field (ADR-078 amendment).
  const skipped      = data?.words_skipped ?? wordsSkipped
  const skippedCode  = data?.skipped_reason  ?? null
  // Honest in both directions and at every moment: words_generated is 0 until
  // the run commits real card rows, so this rises once and never runs
  // backwards, and it is never pinned to 100 to make a finished run look
  // complete when part of it isn't.
  // Words skipped as duplicates are handled, not owed: the cards to make are the
  // confirmed words the deck did not already hold, so "9 / 10" with one duplicate
  // reads 9 / 9, never a 90% shortfall.
  const expected     = Math.max(total - skipped, 0)
  const pct          = expected > 0 ? Math.min(100, Math.round((current / expected) * 100)) : 0

  const isDone          = status === 'done'
  const isFailed        = status === 'failed'
  const isPartialFailed = isDone && failed > 0
  // Every selected word already had a card here, so the import finished with
  // nothing to do. That is neither a success worth a checkmark nor a failure —
  // it is its own outcome, and it has to be said rather than left for the
  // reader to work out by navigating. A word already in the deck stays a skip:
  // it never becomes a pending card, because there is nothing to fill.
  const isNothingToAdd  = isDone && failed === 0 && current === 0
  // Cards exist for every word; some are waiting for content. Not a failure —
  // nothing was lost — but not a clean finish either, so it gets its own
  // outcome rather than being folded into either neighbour.
  const isPending       = isDone && !isPartialFailed && pending > 0
  // Cards exist for every word the deck did not already hold, and the rest were
  // skipped. Nothing failed and nothing is outstanding, so this is not a
  // problem — but part of the selection did not become a card, and the reader
  // has to be able to read why. Its own outcome for the same reason `isPending`
  // is: folding it into full success is what let a short deck redirect away
  // unexplained (ADR-078 amendment).
  const isSkipped       = isDone && !isPartialFailed && !isPending && skipped > 0 && current > 0
  // A clean finish is now clean in all three directions: nothing failed,
  // nothing is waiting for content, and the whole selection became cards.
  const isFullSuccess   = isDone && failed === 0 && pending === 0 && skipped === 0 && current > 0
  const isActive        = !isDone && !isFailed

  const reasonText = (() => {
    if (reasonCode === 'AI_RATE_LIMITED')  return tis.reasonRateLimited
    if (reasonCode === 'AI_UNAVAILABLE')   return tis.reasonUnavailable
    if (reasonCode === 'AI_INCOMPLETE')    return tis.reasonIncomplete
    return tis.reasonUnknown
  })()

  // Why words were skipped. Information, not an error: these words are already
  // in the deck, so there is nothing for the reader to fix or retry. An unknown
  // code still says the count and that they were already there — the count is
  // the part that closes the gap in the arithmetic, and a build that predates a
  // future code must not fall silent about it.
  const skippedText = skippedCode === 'ALREADY_IN_DECK'
    ? tis.skippedAlreadyInDeck(skipped)
    : tis.skippedGeneric(skipped)

  const deckPath  = deckId ? `/decks/${deckId}` : '/decks'
  const learnPath = deckId ? `/decks/${deckId}/learn` : '/decks'
  // No outcome navigates on its own (ADR-078 amendment, 2026-10-20, superseding
  // Decision 4's redirect): the finished screen waits for the learner's choice.

  // After every hook, so the hook order never depends on it. Without an id the
  // status query is disabled and there is no data, so the redirect effect
  // above does nothing.
  if (!textImportId) {
    return (
      <ErrorScreen
        message={tis.noImportId}
        onBack={() => navigate('/text-import')}
        onRetry={null}
      />
    )
  }

  if (isFailed || (isError && errorCountRef.current >= 3)) {
    const msg = isFailed
      ? tis.serverError
      : extractErrorMessage(error, 'Could not reach the server. Check your connection and try again.')

    return (
      <ErrorScreen
        message={msg}
        onBack={() => navigate('/text-import')}
        onRetry={() => {
          errorCountRef.current = 0
          navigate(`/text-import/${textImportId}/review`, { replace: true })
        }}
      />
    )
  }

  if (isNothingToAdd) {
    return (
      <div
        className="min-h-[calc(100vh-64px)] flex flex-col items-center justify-center gap-6 px-4"
        style={{ color: 'var(--text-primary)' }}
      >
        <div
          className="flex items-center justify-center"
          style={{
            width: 84, height: 84, borderRadius: 22,
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            color: 'var(--text-secondary)',
          }}
        >
          <Info size={40} />
        </div>

        <div className="flex flex-col items-center gap-2 text-center max-w-xs">
          <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-primary)' }}>
            {tis.nothingToAddTitle}
          </h1>
          {(targetDeckTitle || title) && (
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {targetDeckTitle ?? title}
            </p>
          )}
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            {skipped > 0 ? tis.nothingToAddDesc(skipped) : tis.nothingToAddPlain}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/text-import')}
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-full font-semibold text-sm transition-all active:scale-95"
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
          >
            <ArrowLeft size={14} />
            {tis.backToImport}
          </button>
          {deckId && (
            <button
              type="button"
              onClick={() => navigate(deckPath, { replace: true })}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all active:scale-95"
              style={{ background: 'var(--accent-fill)', color: 'var(--text-on-accent)' }}
            >
              {tis.openDeck}
            </button>
          )}
        </div>
      </div>
    )
  }

  const stageLabel = (() => {
    const labels: Record<string, string> = {
      extracting: tis.statusAnalysing,
      reviewing:  tis.statusReviewing,
      generating: tis.statusGenerating,
      done:       tis.statusDone,
      failed:     tis.statusFailed,
    }
    return labels[status ?? ''] ?? tis.statusStarting
  })()

  return (
    <div
      className="min-h-[calc(100vh-64px)] flex flex-col items-center justify-center px-4 py-10"
      style={{ color: 'var(--text-primary)' }}
    >
      <div className="w-full max-w-sm flex flex-col items-center gap-7 text-center">

        {/* ── Icon ──────────────────────────────────────────────────────────── */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Glow on success */}
          {isFullSuccess && (
            <div style={{
              position: 'absolute',
              inset: -16,
              borderRadius: '50%',
              background: 'radial-gradient(circle, var(--success-soft) 0%, transparent 70%)',
              pointerEvents: 'none',
            }} />
          )}
          {/* A skip-only run gets the SUCCESS mark, not the warning one: every
              card that was going to be made was made. What it does not get is
              the silent redirect — the count below has to be readable. */}
          <div
            style={{
              width: 84, height: 84,
              borderRadius: 22,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: isPartialFailed || isPending
                ? 'var(--warning-soft)'
                : isFullSuccess || isSkipped
                ? 'var(--success)'
                : 'var(--accent)',
              border: isPartialFailed || isPending ? '1px solid color-mix(in srgb, var(--warning) 30%, transparent)' : 'none',
              color: isPartialFailed || isPending ? 'var(--warning)' : 'var(--text-on-dark)',
              boxShadow: isFullSuccess
                ? '0 4px 20px rgba(39,174,96,0.30)'
                : isActive
                ? 'var(--shadow-accent)'
                : 'none',
            }}
          >
            {isFullSuccess || isSkipped ? (
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : isPartialFailed || isPending ? (
              <AlertCircle size={40} />
            ) : (
              <Loader2 size={40} className="animate-spin" />
            )}
          </div>
        </div>

        {/* ── Headline ──────────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-1.5">
          <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-primary)' }}>
            {isFullSuccess || isSkipped
              ? tis.deckReady
              : isPartialFailed
              ? tis.deckPartial
              : isPending
              ? tis.deckReadyPending
              : tis.buildingDeck}
          </h1>
          {(targetDeckTitle || title) && (
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {targetDeckTitle ? tis.addingToDeck(targetDeckTitle) : title}
            </p>
          )}
          {/* The one-line form, for outcomes that have their own sub-copy
              below (pending, partial failure) or are still in flight. A
              skip-ONLY run says it at full size further down instead — there
              it is the whole news, not a footnote to other news. */}
          {skipped > 0 && !isSkipped && (
            <p className="text-xs" style={{ color: 'var(--text-secondary)', opacity: 0.75 }}>
              {skippedText}
            </p>
          )}
        </div>

        {/* ── Progress card ─────────────────────────────────────────────────── */}
        <div
          className="w-full flex flex-col gap-3 rounded-2xl"
          style={{
            padding: '22px 20px',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          {/* Counter: large numerator / denominator */}
          <div className="flex items-baseline justify-center gap-1">
            <span
              className="tabular-nums"
              style={{
                fontFamily: 'var(--font-content)',
                fontSize: 44, fontWeight: 700, lineHeight: 1,
                color: isDone ? 'var(--success)' : 'var(--accent)',
              }}
            >
              {current}
            </span>
            {expected > 0 && (
              <span
                className="tabular-nums"
                style={{
                  fontFamily: 'var(--font-content)',
                  fontSize: 26, fontWeight: 700,
                  color: 'var(--text-secondary)',
                }}
              >
                {' / '}{expected}
              </span>
            )}
          </div>

          {/* Stage / result label */}
          <p style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-secondary)' }}>
            {isFullSuccess
              ? tis.cardsGenerated(current)
              : isSkipped
              ? tis.cardsCreated(current)
              : isPartialFailed
              ? tis.cardsPartial(current, expected, failed)
              : isPending
              ? `${tis.cardsCreated(current)} — ${tis.cardsWaiting(pending)}`
              : `${stageLabel}`}
          </p>

          {/* Progress bar — created against confirmed, never pinned full.
              A run that finishes with work outstanding must not sit under a
              complete bar; a run still in flight has no card rows yet, so it
              legitimately reads 0 while the spinner above says what is
              happening. */}
          <div
            className="w-full h-2 rounded-full overflow-hidden"
            style={{ background: 'var(--mastery-track)' }}
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={tis.progressAriaLabel}
          >
            <div
              className="h-full rounded-full transition-all duration-500 ease-out"
              style={{
                width: `${pct}%`,
                background: isFullSuccess || isSkipped
                  ? 'var(--success)'
                  : isDone && !isSkipped
                  ? 'var(--warning)'
                  : 'var(--accent)',
              }}
            />
          </div>

          {/* Percentage — omitted while the run is still in flight rather than
              printed as 0%, which reads as a stalled run rather than an
              unfinished one. */}
          {!isActive && (
            <p className="tabular-nums" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
              {`${pct}%`}
            </p>
          )}
        </div>

        {/* ── Sub-copy ──────────────────────────────────────────────────────── */}
        {isActive && (
          <p className="text-sm max-w-xs" style={{ color: 'var(--text-secondary)' }}>
            {tis.generatingDesc}
          </p>
        )}

        {/* Cards created without content — why, and what to do about it.
            The reason is the only thing that differs between these outcomes;
            the promise is the same one in every case: nothing was lost, and
            filling is something the reader starts, not something that happens
            on its own. */}
        {isPending && (
          <div className="flex flex-col items-center gap-3 w-full">
            <p className="text-sm text-center max-w-xs" style={{ color: 'var(--text-secondary)' }}>
              {reasonText}
            </p>
            <p className="text-sm text-center max-w-xs" style={{ color: 'var(--text-secondary)', opacity: 0.8 }}>
              {tis.fillHint}
            </p>
            <button
              type="button"
              onClick={() => navigate(deckPath, { replace: true })}
              className="flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-150 active:scale-95"
              style={{ background: 'var(--accent-fill)', color: 'var(--text-on-accent)' }}
            >
              {tis.openDeck}
            </button>
          </div>
        )}

        {/* A clean finish and a run with skips end the same way: the screen waits
            for the learner's choice, "Study N cards" first and "Open deck" beside
            it. Nothing here navigates on its own (ADR-078 amendment 2026-10-20).

            Some words were already in this deck. Stated plainly, in the
            neutral voice the fact deserves: nothing went wrong, nothing was
            lost, and there is nothing to retry — the cards for those words
            exist already. The reader gets the same Study CTA a clean run gets,
            because the deck is ready; what they do not get is being moved off
            this screen before they have read the sentence explaining why the
            number is smaller than the number they picked. */}
        {(isFullSuccess || isSkipped) && (
          <div className="flex flex-col items-center gap-3 w-full">
            {isSkipped && (
              <p className="text-sm text-center max-w-xs" style={{ color: 'var(--text-secondary)' }}>
                {skippedText}
              </p>
            )}
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => navigate(deckPath, { replace: true })}
                className="flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm transition-all duration-150 active:scale-95"
                style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
              >
                {tis.openDeck}
              </button>
              <button
                type="button"
                onClick={() => navigate(learnPath, { replace: true })}
                className="flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-150 active:scale-95"
                style={{ background: 'var(--accent-fill)', color: 'var(--text-on-accent)' }}
              >
                {tis.studyNCards(current)}
              </button>
            </div>
          </div>
        )}

        {/* Partial failure CTA */}
        {isPartialFailed && (
          <div className="flex flex-col items-center gap-3 w-full">
            <p className="text-sm text-center max-w-xs" style={{ color: 'var(--text-secondary)' }}>
              {tis.partialFailDesc(failed, current)}
            </p>
            <button
              type="button"
              onClick={() => navigate(learnPath, { replace: true })}
              className="flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-150 active:scale-95"
              style={{ background: 'var(--accent-fill)', color: 'var(--text-on-accent)' }}
            >
              {tis.studyNCards(current)}
            </button>
          </div>
        )}

        {/* Back link — only while generating */}
        {isActive && (
          <button
            type="button"
            onClick={() => navigate('/text-import')}
            className="flex items-center gap-2 transition-opacity hover:opacity-70"
            style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <ArrowLeft size={18} />
            {tis.backToImport}
          </button>
        )}
      </div>
    </div>
  )
}

// ── Error screen ──────────────────────────────────────────────────────────────

function ErrorScreen({
  message,
  onBack,
  onRetry,
}: {
  message: string
  onBack: () => void
  onRetry: (() => void) | null
}) {
  const { tr } = useLocale()
  const tis = tr.textImportStatus
  return (
    <div
      className="min-h-[calc(100vh-64px)] flex flex-col items-center justify-center gap-6 px-4"
      style={{ color: 'var(--text-primary)' }}
    >
      <div
        className="flex items-center justify-center w-16 h-16 rounded-2xl"
        style={{ background: 'var(--danger-soft)', color: 'var(--danger)' }}
      >
        <AlertCircle size={32} />
      </div>

      <div className="flex flex-col items-center gap-2 text-center max-w-xs">
        <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
          {tis.errorWentWrong}
        </h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{message}</p>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 px-5 py-2.5 rounded-full font-semibold text-sm transition-all active:scale-95"
          style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
        >
          <ArrowLeft size={14} />
          {tis.startOver}
        </button>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all active:scale-95"
            style={{ background: 'var(--accent-fill)', color: 'var(--text-on-accent)' }}
          >
            {tis.retryBtn}
          </button>
        )}
      </div>
    </div>
  )
}
